/**
 * Avatar — circular user/entity representation.
 *
 * Design system mapping (mch-avatar):
 *   • Default bg:   bg-webex-gray-700 / text-white
 *   • Sizes:        24=size-6 text-[10px] · 32=size-8 text-xs · 40=size-10 text-sm · 56=size-14 text-base
 *   • Color tokens: cobalt/cyan/gold/lime/mint/orange/pink/purple/slate/violet/yellow
 *                   (each uses *-70 from the Webex palette per design-system spec)
 *
 * Built on Base UI <Avatar> for proper image-fallback handling.
 *
 * Structure (when status is present):
 *   <span class="relative">         ← outer wrapper (no clipping)
 *     <BaseAvatar.Root class="overflow-hidden rounded-full">  ← clips image
 *       …image / initials / icon…
 *     </BaseAvatar.Root>
 *     <span class="absolute …" />   ← status dot, sits outside the clip
 *   </span>
 */
import * as React from "react"
import { Avatar as BaseAvatar } from "@base-ui-components/react/avatar"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const avatarVariants = cva(
  "inline-flex items-center justify-center rounded-full font-medium text-white overflow-hidden select-none align-middle",
  {
    variants: {
      size: {
        24: "size-6 text-[10px]",
        32: "size-8 text-xs",
        40: "size-10 text-sm",
        56: "size-14 text-base",
      },
      color: {
        default: "bg-webex-gray-700",
        cobalt: "bg-webex-cobalt-700",
        cyan: "bg-webex-cyan-700",
        gold: "bg-webex-gold-700",
        lime: "bg-webex-lime-700",
        mint: "bg-webex-mint-700",
        orange: "bg-webex-orange-700",
        pink: "bg-webex-pink-700",
        purple: "bg-webex-purple-700",
        slate: "bg-webex-slate-700",
        violet: "bg-webex-violet-700",
        yellow: "bg-webex-yellow-700",
      },
    },
    defaultVariants: {
      size: 32,
      color: "default",
    },
  },
)

const statusDotVariants = cva(
  // Anchored to the bottom-right of the wrapper. The negative offset pulls the
  // dot's centre onto the avatar's outer edge so it visually overlaps without
  // being clipped (the wrapper has no overflow-hidden).
  "absolute bottom-0 right-0 rounded-full ring-2 ring-white dark:ring-webex-gray-950 translate-x-[15%] translate-y-[15%]",
  {
    variants: {
      size: {
        24: "size-1.5",
        32: "size-2",
        40: "size-2.5",
        56: "size-3.5",
      },
      status: {
        online: "bg-webex-green-500",
        offline: "bg-webex-gray-500",
        dnd: "bg-webex-red-600",
        away: "bg-webex-yellow-500",
      },
    },
    defaultVariants: {
      size: 32,
      status: "offline",
    },
  },
)

const wrapperSize: Record<24 | 32 | 40 | 56, string> = {
  24: "size-6",
  32: "size-8",
  40: "size-10",
  56: "size-14",
}

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  initials?: string
  icon?: React.ReactNode
  status?: "online" | "offline" | "dnd" | "away"
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    { className, size = 32, color = "default", src, alt, initials, icon, status, ...props },
    ref,
  ) => {
    const sizeKey = (size ?? 32) as 24 | 32 | 40 | 56
    const root = (
      <BaseAvatar.Root
        ref={status ? undefined : ref}
        className={cn(avatarVariants({ size, color }), status && "block", status ? "" : className)}
        {...(status ? {} : props)}
      >
        {src ? (
          <>
            <BaseAvatar.Image
              src={src}
              alt={alt ?? initials ?? ""}
              className="size-full object-cover"
            />
            <BaseAvatar.Fallback className="flex size-full items-center justify-center">
              {initials ?? icon ?? <DefaultUserIcon />}
            </BaseAvatar.Fallback>
          </>
        ) : (
          <span aria-hidden={!alt && !initials} className="flex size-full items-center justify-center">
            {initials ?? icon ?? <DefaultUserIcon />}
          </span>
        )}
      </BaseAvatar.Root>
    )

    if (!status) return root

    return (
      <span
        ref={ref}
        className={cn(
          "relative inline-flex shrink-0 align-middle",
          wrapperSize[sizeKey],
          className,
        )}
        {...props}
      >
        {root}
        <span
          className={statusDotVariants({ size, status })}
          role="img"
          aria-label={`Status: ${status}`}
        />
      </span>
    )
  },
)
Avatar.displayName = "Avatar"

function DefaultUserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-3/5" aria-hidden="true">
      <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z" />
    </svg>
  )
}
